import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import GlassCard from '@/components/base/GlassCard';
import {
  dailyApplicationsTrend,
  approvalRateData,
  serviceCategoryData,
  funnelData,
  hourlyHeatmapData,
} from '@/mocks/dashboardData';

const DAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const HOURS = Array.from({ length: 24 }, (_, i) => `${i}:00`);

const getHeatColor = (value: number) => {
  const min = 0;
  const max = 180;
  const ratio = (value - min) / (max - min);
  if (ratio < 0.25) return `rgba(255,77,77,${0.1 + ratio * 0.4})`;
  if (ratio < 0.5) return `rgba(255,100,50,${0.25 + ratio * 0.4})`;
  if (ratio < 0.75) return `rgba(255,140,66,${0.5 + ratio * 0.3})`;
  return `rgba(255,180,100,${0.7 + ratio * 0.3})`;
};

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg px-3 py-2 text-xs" style={{ background: '#1a1a2e', border: '1px solid rgba(255,77,77,0.3)' }}>
        <p className="text-white font-medium mb-1">{label}</p>
        {payload.map((p) => (
          <p key={p.name} style={{ color: p.color }}>{p.name}: {p.value}</p>
        ))}
      </div>
    );
  }
  return null;
};

const DashboardCharts: React.FC = () => {
  return (
    <div className="space-y-4">
      {/* Line + Bar */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <GlassCard className="lg:col-span-3" title="Günlük Başvuru Trendi" subtitle="Son 7 gün">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={dailyApplicationsTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="gun" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '11px', color: '#9ca3af' }} />
              <Line type="monotone" dataKey="basvuru" name="Başvuru" stroke="#ff4d4d" strokeWidth={2} dot={{ r: 3, fill: '#ff4d4d' }} />
              <Line type="monotone" dataKey="onaylanan" name="Onaylanan" stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981' }} />
              <Line type="monotone" dataKey="reddedilen" name="Reddedilen" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: '#f59e0b' }} />
            </LineChart>
          </ResponsiveContainer>
        </GlassCard>

        <GlassCard className="lg:col-span-2" title="Kabul Oranı (%)" subtitle="Son 6 ay">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={approvalRateData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="ay" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} domain={[0, 50]} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="oran" name="Oran %" radius={[4, 4, 0, 0]}>
                {approvalRateData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={`url(#barGrad${index})`} />
                ))}
              </Bar>
              <defs>
                {approvalRateData.map((_, i) => (
                  <linearGradient key={i} id={`barGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff4d4d" />
                    <stop offset="100%" stopColor="#ff8c42" />
                  </linearGradient>
                ))}
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>
      </div>

      {/* Funnel + Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <GlassCard className="lg:col-span-3" title="Lead Dönüşüm Funnel" subtitle="Reklam → Tamamlanan İş">
          <div className="flex items-end justify-center gap-2 h-44">
            {funnelData.map((item, idx) => {
              const maxVal = funnelData[0].sayi;
              const heightPct = (item.sayi / maxVal) * 100;
              const widthPct = 100 - idx * 10;
              return (
                <div key={idx} className="flex flex-col items-center gap-1.5 flex-1">
                  <div className="text-center">
                    <div className="text-white font-bold text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {item.sayi.toLocaleString('tr-TR')}
                    </div>
                    <div className="text-xs" style={{ color: '#6b7280' }}>
                      {idx > 0 ? `%${Math.round((item.sayi / funnelData[0].sayi) * 100)}` : '%100'}
                    </div>
                  </div>
                  <div
                    className="w-full rounded-t-lg transition-all duration-700"
                    style={{
                      height: `${heightPct}%`,
                      maxHeight: '80px',
                      minHeight: '20px',
                      background: `linear-gradient(180deg, ${item.renk} 0%, ${item.renk}88 100%)`,
                      clipPath: `polygon(${(100 - widthPct) / 2}% 0%, ${100 - (100 - widthPct) / 2}% 0%, 100% 100%, 0% 100%)`,
                    }}
                  />
                  <span className="text-xs text-center leading-tight" style={{ color: '#9ca3af', fontSize: '10px' }}>
                    {item.aşama}
                  </span>
                </div>
              );
            })}
          </div>
        </GlassCard>

        <GlassCard className="lg:col-span-2" title="Hizmet Kategorisi" subtitle="Dağılım">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={serviceCategoryData}
                cx="50%"
                cy="45%"
                innerRadius={45}
                outerRadius={75}
                paddingAngle={3}
                dataKey="value"
              >
                {serviceCategoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => [`%${value}`, '']}
                contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,77,77,0.3)', borderRadius: '8px', fontSize: '12px' }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 mt-1">
            {serviceCategoryData.map((item) => (
              <div key={item.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.color }} />
                  <span style={{ color: '#9ca3af' }}>{item.name}</span>
                </div>
                <span className="font-medium text-white">%{item.value}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      {/* Heatmap */}
      <GlassCard title="Saatlik Talep Yoğunluğu" subtitle="Son 7 gün — renk yoğunluğu talep sayısını gösterir">
        <div className="overflow-x-auto">
          <div className="min-w-[700px]">
            {/* Hour labels */}
            <div className="flex mb-1 ml-10">
              {HOURS.map((h, i) => (
                <div key={i} className="flex-1 text-center text-gray-600" style={{ fontSize: '8px', minWidth: '20px' }}>
                  {i % 3 === 0 ? h : ''}
                </div>
              ))}
            </div>
            {/* Rows */}
            {DAYS.map((day, dayIdx) => (
              <div key={day} className="flex items-center gap-1 mb-1">
                <div className="w-8 text-right text-xs flex-shrink-0 pr-1" style={{ color: '#6b7280', fontSize: '10px' }}>{day}</div>
                <div className="flex gap-0.5 flex-1">
                  {hourlyHeatmapData[dayIdx].map((val, hourIdx) => (
                    <div
                      key={hourIdx}
                      className="rounded-sm flex-1 cursor-default transition-transform hover:scale-110 relative group"
                      style={{ height: '16px', minWidth: '16px', background: getHeatColor(val) }}
                      title={`${day} ${HOURS[hourIdx]}: ${val} talep`}
                    />
                  ))}
                </div>
              </div>
            ))}
            {/* Legend */}
            <div className="flex items-center gap-2 mt-2 justify-end">
              <span className="text-xs" style={{ color: '#6b7280' }}>Az</span>
              {[0.1, 0.3, 0.5, 0.7, 0.9].map((ratio) => (
                <div
                  key={ratio}
                  className="rounded-sm"
                  style={{ width: '16px', height: '12px', background: `rgba(255,120,60,${ratio})` }}
                />
              ))}
              <span className="text-xs" style={{ color: '#6b7280' }}>Çok</span>
            </div>
          </div>
        </div>
      </GlassCard>
    </div>
  );
};

export default DashboardCharts;
